import React, { Fragment } from 'react'
import {
  ActionButton,
  Button,
  Checkbox,
  InputField,
  Select,
  ToggleSwitch,
} from '@enamelai/design-system'
import { Icon as Delete2Icon } from '@enamelai/design-system/icons/Delete2'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'
import UnsavedChangesModal from '@components/pages/Settings/Communications/UnsavedChangesModal/UnsavedChangesModal.component'
import {
  amPmLabelToHms,
  buildTimeOptionsAmPm,
  formatHmsAsAmPm,
} from '@utils/phoneManagement.utils'
import { IClinicNumber } from '@dataTypes/clinic.type'
import {
  fullDaysOfWeek,
  TIMEZONE_OPTIONS,
} from '@constants/Common/date.constant'
import useWorkingHours from './useWorkingHours'
import useCommunicationsLeaveGuard from '../../shared/useCommunicationsLeaveGuard'

const TIME_OPTS = buildTimeOptionsAmPm(30)
const TIME_OPTIONS = TIME_OPTS.map((time) => ({ label: time, value: time }))
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

const isoToDisplayDate = (value: string): string => {
  const match = ISO_DATE_RE.exec(value.trim())
  if (!match) return value
  return `${match[2]} / ${match[3]} / ${match[1]}`
}

const displayDateToIso = (value: string): string => {
  const match = value.trim().match(/^(\d{2}) \/ (\d{2}) \/ (\d{4})$/)
  if (!match) return value.trim()
  return `${match[3]}-${match[1]}-${match[2]}`
}

const formatIsoDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export type WorkingHoursPanelProps = {
  phoneLabel: string
  activeNumber: IClinicNumber
  selectedNumbers: IClinicNumber[]
  clinicNumbers: IClinicNumber[]
  updateActiveNumber: (
    data: Partial<IClinicNumber & { timezone?: string }>,
  ) => void
  onClinicNumbersSaved?: (updated: IClinicNumber[]) => void
  onNext: () => void
  onCancel: () => void
  workingHoursDataDirty: boolean
}

const WorkingHoursPanel = (
  props: WorkingHoursPanelProps,
): React.ReactElement => {
  const hooks = useWorkingHours(props)
  const leaveGuard = useCommunicationsLeaveGuard(
    hooks.hasWorkingHoursChangesToSave,
  )

  const title = (
    <>
      <span>
        Working hours (<span className="scp-subtitle">{props.phoneLabel}</span>)
      </span>
      <span className="scp-step-count">
        {hooks.currentIndex + 1} of {props.selectedNumbers.length}
      </span>
    </>
  )

  return (
    <div className="scp-call-flow-side-panel">
      <div className="scp-panel-title">{title}</div>
      <div className="scp-panel-body">
        <div className="scp-panel-field">
          <Select
            label="Timezone"
            options={TIMEZONE_OPTIONS}
            value={hooks.timezone}
            onChange={(value) => hooks.updateTimezone(String(value))}
          />
        </div>
        <div className="scp-panel-field">
          <span className="scp-panel-label">Working hours</span>
          <div className="scp-row-container scp-working-hours-panel">
            {fullDaysOfWeek.map((day) => {
              const row = props.activeNumber.workingHours?.find(
                (hours) => hours.day_of_week === day,
              )
              const dayInvalid =
                !!row && hooks.validationSnapshot?.invalidWeekdays.has(day)
              return (
                <Fragment key={day}>
                  <div className="scp-switch-label">
                    <ToggleSwitch
                      checked={!!row}
                      aria-label={`${day} working hours`}
                      onChange={(checked) =>
                        hooks.updateDay(day, { on: checked })
                      }
                    />
                    <span>{day}</span>
                  </div>
                  <Select
                    className="scp-wh-time-select"
                    label=""
                    leadingIcon="clock"
                    disabled={!row}
                    error={dayInvalid}
                    options={TIME_OPTIONS}
                    value={formatHmsAsAmPm(row?.start_time ?? '08:00:00')}
                    onChange={(value) =>
                      hooks.updateDay(day, {
                        start: amPmLabelToHms(String(value)),
                      })
                    }
                  />
                  <Select
                    className="scp-wh-time-select"
                    label=""
                    leadingIcon="clock"
                    disabled={!row}
                    error={dayInvalid}
                    options={TIME_OPTIONS}
                    value={formatHmsAsAmPm(row?.end_time ?? '17:00:00')}
                    onChange={(value) =>
                      hooks.updateDay(day, {
                        end: amPmLabelToHms(String(value)),
                      })
                    }
                  />
                </Fragment>
              )
            })}
          </div>

          <div className="scp-bottom-button-wrapper">
            <Checkbox
              label="Same working hours"
              checked={!!props.activeNumber.same_working_hours}
              onChange={(event) =>
                props.updateActiveNumber({
                  same_working_hours: event.target.checked,
                })
              }
            />
          </div>
          {hooks.validationSnapshot &&
            hooks.validationSnapshot.invalidWeekdays.size > 0 && (
              <span className="hint-text" role="alert">
                Start time must be before end time.
              </span>
            )}
        </div>

        <div className="scp-panel-field">
          <span className="scp-panel-label">Lunch break</span>
          <div className="scp-row">
            <Select
              className="scp-wh-time-select"
              label=""
              leadingIcon="clock"
              error={!!hooks.validationSnapshot?.lunchInvalid}
              options={TIME_OPTIONS}
              value={formatHmsAsAmPm(hooks.lunch.start_time ?? '12:00:00')}
              onChange={(value) =>
                hooks.updateLunch('start_time', String(value))
              }
            />
            <Select
              className="scp-wh-time-select"
              label=""
              leadingIcon="clock"
              error={!!hooks.validationSnapshot?.lunchInvalid}
              options={TIME_OPTIONS}
              value={formatHmsAsAmPm(hooks.lunch.end_time ?? '13:00:00')}
              onChange={(value) => hooks.updateLunch('end_time', String(value))}
            />
          </div>
          {hooks.validationSnapshot?.lunchInvalid ? (
            <span className="hint-text" role="alert">
              Start time must be before end time for lunch break.
            </span>
          ) : null}
        </div>
        <div className="scp-panel-field">
          <div className="scp-label-row">
            <span className="scp-panel-label">Holidays / day-offs</span>
            <button
              type="button"
              className="ds-section-card__icon-button"
              title="Add overrides for holidays when the clinic is closed."
              aria-label="Holidays and day-offs information"
            >
              <InfoIcon aria-hidden="true" />
            </button>
          </div>
          <div className="scp-row-container">
            {hooks.overrides.map((override) => {
              const overrideInvalid =
                !!override.working_hours_id &&
                hooks.validationSnapshot?.invalidOverrideIds.has(
                  override.working_hours_id,
                )
              const commitDate = (raw: string, date?: Date): void => {
                hooks.updateOverride(override.working_hours_id!, {
                  day_of_week: date
                    ? formatIsoDate(date)
                    : displayDateToIso(raw),
                  start_time: '00:00:00',
                  end_time: '23:59:59',
                })
              }
              return (
                <div className="scp-row" key={override.working_hours_id}>
                  <InputField
                    className="scp-inline-field"
                    variant="text"
                    fullWidth
                    label=""
                    aria-label="Holiday name"
                    clearable={false}
                    value={override.name ?? ''}
                    onChange={(event) =>
                      hooks.updateOverride(override.working_hours_id!, {
                        name: event.target.value,
                      })
                    }
                  />
                  <div className="scp-date-field">
                    <InputField
                      className="scp-inline-field"
                      variant="date"
                      fullWidth
                      label=""
                      aria-label="Holiday date"
                      clearable={false}
                      value={isoToDisplayDate(override.day_of_week)}
                      error={overrideInvalid}
                      onChange={(event) => commitDate(event.target.value)}
                      onDateChange={(date) => commitDate('', date)}
                    />
                  </div>
                  <ActionButton
                    size="sm"
                    tone="danger"
                    variant="outline"
                    label="Delete"
                    icon={
                      <Delete2Icon
                        className="ds-action-button__svg"
                        aria-hidden="true"
                      />
                    }
                    onClick={() =>
                      hooks.removeOverride(override.working_hours_id!)
                    }
                  />
                </div>
              )
            })}
          </div>
          {hooks.validationSnapshot &&
            hooks.validationSnapshot.invalidOverrideIds.size > 0 && (
              <span className="hint-text" role="alert">
                Holiday / day-off dates cannot be in the past.
              </span>
            )}
          <div className="scp-bottom-button-wrapper">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="add-overrides-btn"
              onClick={hooks.addOverride}
            >
              Add holidays
            </Button>
          </div>
        </div>
      </div>

      <div className="scp-panel-footer">
        <div className="scp-bottom-button-wrapper">
          {props.selectedNumbers.length > 1 && (
            <Checkbox
              label="Apply to all numbers"
              checked={hooks.applyToAllNumbers}
              onChange={(event) =>
                hooks.setApplyToAllNumbers(event.target.checked)
              }
            />
          )}
        </div>
        <div className="component-buttons">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={hooks.handleCancelOrClose}
            aria-label={
              props.workingHoursDataDirty || hooks.applyToAllNumbers
                ? 'Cancel'
                : 'Close'
            }
          >
            {props.workingHoursDataDirty || hooks.applyToAllNumbers
              ? 'Cancel'
              : 'Close'}
          </Button>
          {hooks.showNext ? (
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={hooks.handleNext}
            >
              Next
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="primary"
              disabled={!hooks.hasWorkingHoursChangesToSave}
              title={
                !hooks.hasWorkingHoursChangesToSave
                  ? 'No changes to save for the selected number(s).'
                  : undefined
              }
              onClick={hooks.onSave}
            >
              Save
            </Button>
          )}
        </div>
      </div>
      {leaveGuard.modal}
      <UnsavedChangesModal
        show={hooks.showUnsavedCloseModal}
        onConfirm={hooks.discardUnsavedChanges}
        onHide={hooks.onHideUnsavedCloseModal}
      />
    </div>
  )
}

export default WorkingHoursPanel
